import cn from 'classnames';
// React
import { useRef } from 'react';
// Types
import Slider from 'react-slick';
// API
import { STREAMS } from 'constants/routes';
// Types
import { UserRole } from 'api/graphql-global-types';
// Hooks
import {
  useAthleteRelatedStreams,
  useBreakpoints,
  useCarouselArrows,
} from 'hooks';
// Components
import RelatedStreams from './RelatedStreams/RelatedStreams';
import SectionHeader from 'components/common3/SectionHeader/SectionHeader';
// Styles
import styles from './Streams.module.scss';
import LiveStream from './LiveStream/LiveStream';

type StreamsProps = {
  storeId: string;
  storeSlug: string | null;
  storeRole: UserRole;
  className?: string;
  link?: string;
  hasActiveStream?: boolean;
  arrowsBelowOnMobile?: boolean;
  displayLiveStream?: boolean;
};

const Streams = ({
  storeId,
  storeSlug,
  storeRole,
  className,
  link,
  hasActiveStream = false,
  arrowsBelowOnMobile = false,
  displayLiveStream = true,
}: StreamsProps) => {
  const sliderRef = useRef<InstanceType<typeof Slider> | null>(null);
  const { streams } = useAthleteRelatedStreams({ storeId });
  const { isBreakpointL } = useBreakpoints();

  const [featuredStream, ...relatedStreams] = streams;
  const visibleStreams = hasActiveStream ? relatedStreams : streams;
  const { canScroll, atStart, atEnd, onBeforeChange } = useCarouselArrows(
    sliderRef,
    visibleStreams.length
  );

  const seeAllLink = link
    ? link
    : storeSlug && storeRole === UserRole.Athlete
    ? `${STREAMS}?athlete=${storeSlug}`
    : storeId && storeRole === UserRole.Organization
    ? `${STREAMS}?organizationIds=${storeId}`
    : storeRole === UserRole.ContentCreator
    ? `${STREAMS}?contentCreatorIds=${storeId}`
    : STREAMS;

  const handlePrevClick = () => {
    sliderRef.current?.slickPrev();
  };

  const handleNextClick = () => {
    sliderRef.current?.slickNext();
  };

  if (!streams.length) {
    return null;
  }

  return (
    <>
      {hasActiveStream && displayLiveStream && (
        <LiveStream stream={featuredStream} />
      )}
      <section className={cn(styles.section, className)}>
        <SectionHeader
          title="Epic Streams"
          onPrev={
            arrowsBelowOnMobile && !isBreakpointL ? undefined : handlePrevClick
          }
          onNext={
            arrowsBelowOnMobile && !isBreakpointL ? undefined : handleNextClick
          }
          seeAllLink={seeAllLink}
          seeAllLinkTitle="See Channel"
          showArrows={arrowsBelowOnMobile && !isBreakpointL ? false : canScroll}
          prevDisabled={atStart}
          nextDisabled={atEnd}
        />
        <RelatedStreams
          ref={sliderRef}
          streams={visibleStreams}
          arrowsBelowOnMobile={arrowsBelowOnMobile}
          onPrev={handlePrevClick}
          onNext={handleNextClick}
          showArrows={canScroll}
          beforeChange={onBeforeChange}
        />
      </section>
    </>
  );
};

export default Streams;
